/**
 * Day strip + time grid. Taken times stay visible and are clearly marked; the server
 * re-checks everything on submit. Shared by booking, client reschedule and the cabinet.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@astryxdesign/core/Button';
import { Carousel } from '@astryxdesign/core/Carousel';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Grid } from '@astryxdesign/core/Grid';
import { VStack } from '@astryxdesign/core/Layout';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { Text } from '@astryxdesign/core/Text';
import { ToggleButton } from '@astryxdesign/core/ToggleButton';
import type { Slot } from '@shared/contracts.ts';
import { addDays } from '@shared/period.ts';
import { dayLabel, dayLong, time } from '@/lib/format.ts';
import { ErrorView } from '@/components/StateViews.tsx';
import type { DayOption } from './days.ts';
import { useIndustry } from '@/lib/industry.ts';

interface Props {
  days: DayOption[];
  day: string | null;
  onDayChange: (day: string) => void;
  selected: string | null;
  onSelect: (slot: Slot) => void;
  timezone: string;
  queryKey: unknown[];
  fetchDay: (day: string, days: number) => Promise<Slot[]>;
}

export function SlotPicker({ days, day, onDayChange, selected, onSelect, timezone, queryKey, fetchDay }: Props) {
  const copy = useIndustry();
  const firstOpen = days.find((d) => d.isOpen)?.day ?? days[0]?.day ?? null;
  const current = day ?? firstOpen;

  useEffect(() => {
    if (!day && firstOpen) onDayChange(firstOpen);
  }, [day, firstOpen, onDayChange]);

  const slots = useQuery({
    queryKey: [...queryKey, current],
    queryFn: () => fetchDay(current!, 1),
    enabled: Boolean(current),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  const next = useQuery({
    queryKey: [...queryKey, 'next-free', current],
    queryFn: async () => (await fetchDay(addDays(current!, 1), 14)).find((s) => s.free) ?? null,
    enabled: Boolean(current) && slots.isSuccess && !slots.data.some((s) => s.free),
  });

  const dayInfo = useMemo(() => days.find((d) => d.day === current), [days, current]);

  return (
    <VStack gap={4}>
      <Carousel gap={2} hasButtons={false} aria-label="Дата">
        {days.map((d) => {
          const [wd, rest] = dayLabel(d.day).split(', ');
          return (
            <ToggleButton
              key={d.day}
              label={`${dayLong(d.day)}${d.isOpen ? '' : ' — выходной'}`}
              isPressed={d.day === current}
              onPressedChange={() => onDayChange(d.day)}
              size="lg"
            >
              <VStack gap={0} hAlign="center">
                {/* The picked chip is solid white (theme), so its text follows the chip color. */}
                <Text type="supporting" color={!d.isOpen ? 'disabled' : d.day === current ? 'inherit' : 'secondary'}>
                  {wd}
                </Text>
                <Text weight="semibold" color={!d.isOpen ? 'disabled' : d.day === current ? 'inherit' : 'primary'} hasStrikethrough={!d.isOpen}>
                  {rest}
                </Text>
              </VStack>
            </ToggleButton>
          );
        })}
      </Carousel>

      {current && (
        <Text type="supporting" aria-live="polite">
          {dayLong(current)}
          {dayInfo?.note ? ` · ${dayInfo.note}` : ''}
        </Text>
      )}

      {slots.isPending ? (
        <Grid columns={{ minWidth: 76, repeat: 'fill' }} gap={2} aria-busy="true">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} index={i} height={44} />
          ))}
        </Grid>
      ) : slots.isError ? (
        <ErrorView error={slots.error} onRetry={() => slots.refetch()} />
      ) : slots.data.length === 0 ? (
        <EmptyState
          isCompact
          title={dayInfo && !dayInfo.isOpen ? copy.closedDay : 'На этот день записи нет'}
          description="Выберите другой день."
          actions={<NextFree query={next} onPick={(s) => (onDayChange(s.day), onSelect(s))} />}
        />
      ) : (
        <VStack gap={3}>
          <Grid columns={{ minWidth: 76, repeat: 'fill' }} gap={2} role="group" aria-label="Время">
            {slots.data.map((s) => (
              <ToggleButton
                key={s.starts_at}
                label={s.free ? time(s.starts_at, timezone) : `${time(s.starts_at, timezone)} — занято`}
                isPressed={selected === s.starts_at}
                isDisabled={!s.free}
                onPressedChange={() => onSelect(s)}
              >
                <Text hasStrikethrough={!s.free} color={s.free ? 'inherit' : 'disabled'} hasTabularNumbers>
                  {time(s.starts_at, timezone)}
                </Text>
              </ToggleButton>
            ))}
          </Grid>
          {!slots.data.some((s) => s.free) && (
            <VStack gap={2}>
              <Text type="supporting">Всё время на этот день занято.</Text>
              <NextFree query={next} onPick={(s) => (onDayChange(s.day), onSelect(s))} />
            </VStack>
          )}
        </VStack>
      )}
    </VStack>
  );
}

function NextFree({ query, onPick }: { query: { data?: Slot | null; isPending: boolean; isFetching: boolean }; onPick: (s: Slot) => void }) {
  const copy = useIndustry();
  if (query.data) return <Button label={`Ближайшее свободное: ${dayLabel(query.data.day)}`} variant="primary" onClick={() => onPick(query.data!)} />;
  if (query.isFetching) return <Button label="Ищем ближайшее время…" isLoading />;
  if (query.data === null) return <Text type="supporting">В ближайшие две недели свободного времени нет — позвоните в {copy.placeAcc}.</Text>;
  return null;
}
